import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CheckpointPanel = ({ checkpoint, visitedCount, totalCount, onContinue, onViewInPortfolio }) => {
  if (!checkpoint) return null;

  const { code, eyebrow, title, subtitle, body, type, color, stats, skillGroups, projects, contacts } = checkpoint;

  return (
    <AnimatePresence>
      <motion.div
        className="cp-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        role="dialog"
        aria-modal="true"
        aria-label={`Portfolio section: ${title}`}
      >
        <motion.div
          className="cp-panel"
          initial={{ y: 60, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 40, opacity: 0, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          style={{ '--cp-color': color }}
        >
          {/* Glow accent */}
          <div className="cp-glow" />

          {/* Header */}
          <motion.div
            className="cp-header"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <div className="cp-eyebrow">
              <span className="cp-code" style={{ color }}>{code}</span>
              <span className="cp-sep">·</span>
              <span>{eyebrow}</span>
            </div>
            <div className="cp-progress-row">
              <div className="cp-progress-dots">
                {Array.from({ length: totalCount }).map((_, i) => (
                  <div
                    key={i}
                    className={`cp-dot${i < visitedCount ? ' cp-dot--done' : ''}${i === visitedCount - 1 ? ' cp-dot--current' : ''}`}
                    style={i < visitedCount ? { background: color, boxShadow: `0 0 8px ${color}88` } : {}}
                  />
                ))}
              </div>
              <span className="cp-progress-label" style={{ color }}>{visitedCount}/{totalCount}</span>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h2
            className="cp-title"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.4 }}
          >
            {title}
          </motion.h2>
          {subtitle && (
            <motion.div
              className="cp-subtitle"
              style={{ color }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.24, duration: 0.4 }}
            >
              {subtitle}
            </motion.div>
          )}

          {/* Body text */}
          <motion.p
            className="cp-body"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            {body}
          </motion.p>

          {/* Type-specific content, each entry cascades in rather than
              appearing all at once */}
          {type === 'about' && stats && (
            <div className="cp-stats">
              {stats.map((s, i) => (
                <motion.div
                  className="cp-stat"
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.38 + i * 0.06, duration: 0.35 }}
                >
                  <div className="cp-stat-number" style={{ color }}>{s.number}</div>
                  <div className="cp-stat-label">{s.label}</div>
                </motion.div>
              ))}
            </div>
          )}

          {type === 'skills' && skillGroups && (
            <div className="cp-skills">
              {skillGroups.map((group, gi) => (
                <motion.div
                  className="cp-skill-group"
                  key={gi}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.38 + gi * 0.08, duration: 0.35 }}
                >
                  <div className="cp-skill-group-title">{group.category}</div>
                  <div className="cp-skill-tags">
                    {group.items.map((item, ii) => (
                      <motion.span
                        className="cp-skill-tag"
                        key={ii}
                        style={{ borderColor: `${color}44`, color }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.42 + gi * 0.08 + ii * 0.03, duration: 0.3 }}
                      >
                        {item}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {type === 'projects' && projects && (
            <div className="cp-projects">
              {projects.map((p, pi) => (
                <motion.div
                  className="cp-project"
                  key={pi}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.38 + pi * 0.08, duration: 0.35 }}
                >
                  <div className="cp-project-stamp" style={{ color }}>{p.stamp}</div>
                  <div className="cp-project-body">
                    <div className="cp-project-name">{p.name}</div>
                    <div className="cp-project-tech">{p.tech}</div>
                    <div className="cp-project-desc">{p.desc}</div>
                    <div className="cp-project-links">
                      {p.live && p.live !== '#' && (
                        <a href={p.live} target="_blank" rel="noopener noreferrer" className="cp-link" style={{ color }}>
                          Live ↗
                        </a>
                      )}
                      {p.github && p.github !== '#' && (
                        <a href={p.github} target="_blank" rel="noopener noreferrer" className="cp-link" style={{ color }}>
                          GitHub ↗
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {type === 'contact' && contacts && (
            <div className="cp-contacts">
              {contacts.map((c, ci) => (
                <motion.a
                  href={c.href}
                  key={ci}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-contact-link"
                  style={{ borderColor: `${color}44` }}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -3, scale: 1.03 }}
                  transition={{ delay: 0.38 + ci * 0.07, duration: 0.35 }}
                >
                  <span className="cp-contact-icon">{c.icon}</span>
                  <span style={{ color }}>{c.label}</span>
                </motion.a>
              ))}
            </div>
          )}

          {/* Actions */}
          <motion.div
            className="cp-actions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.4 }}
          >
            <motion.button
              className="cp-btn cp-btn--view"
              onClick={onViewInPortfolio}
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              style={{ borderColor: `${color}66`, color }}
            >
              View in Portfolio ↗
            </motion.button>
            <motion.button
              className="cp-btn cp-btn--continue"
              onClick={onContinue}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              style={{ background: color }}
              autoFocus
            >
              Continue Flight →
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CheckpointPanel;